import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";

// Components
import Layout from "../../../Layout/Layout";
import { InputField } from "../../../Components/formElements/InputField";
import { ComboboxField } from "../../../Components/formElements/ComboboxField";
import { TextAreaField } from "../../../Components/formElements/TextAreaField";
import SuccessRegisterModal from "../../../Components/SuccessRegisterModal";
import FailureModal from "../../../Components/Modal/supplier_app/FailureModal";
import Constants from "../../../common/SupplierConstants";
import { getCurrentDate } from "../../../common/Validations";
import ItemService from "../../../services/supplier_appCommonService";

import {
  getRequestTypeOptions,
  getBusinessUnitOptions,
  getItemCategoryOptions,
  getUomOptions,
} from "./SampleConstant";

const CreateSampleRequest = () => {
  const navigate = useNavigate();

  localStorage.setItem("menu", "Sampling Process");
  localStorage.setItem("submenu", "Sampling Creation");

  const [currentDate] = useState(getCurrentDate("default"));
  const [isCardVisible, setCardVisible] = useState(true);
  const [disabled, setDisabled] = useState(false);

  // Form inputs state
  const [formInputs, setFormInputs] = useState({
    requestType: "",
    requestTypeCode: "",
    businessUnit: "",
    businessUnitCode: "",
    itemCategory: "",
    itemCategoryCode: "",
    itemDescription: "",
    expectedSampleDate: getCurrentDate("short"),
    sampleQuantity: "",
    sampleUom: "",
    sampleUomCode: "",
    remarks: "",
    status: "Pending Artwork",
  });

  const [validation, setValidation] = useState({});
  const [getShowSuccessModal, setShowSuccessModal] = useState(false);
  const [getModalInfo, setModalInfo] = useState({});
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getFailureModal, setFailureModal] = useState({});
  const [getErrorFields, setErrorFields] = useState([]);

  const toggleCardVisibility = () => {
    setCardVisible(!isCardVisible);
  };

  const [getDropdownData, setDropdownData] = useState("");

  useEffect(() => {
    // ComboboxField reports initial state (with no `name`) on first render.
    // Ignore that to preserve initial values. A real user selection always has a `name`.
    if (!getDropdownData?.name) return;

    setFormInputs((prev) => ({
      ...prev,
      [getDropdownData.textField]: getDropdownData.name,
      ...(getDropdownData.textCode ? { [getDropdownData.textCode]: getDropdownData.code } : {}),
    }));

    // Clear validation error when user selects an option
    if (getDropdownData.textField) {
      setValidation((prev) => ({
        ...prev,
        [getDropdownData.textField]: "",
      }));
    }
  }, [getDropdownData]);

  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validation[name]) {
      setValidation((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const errorList = [];

    if (!formInputs.requestType) {
      errors.requestType = "Request Type is required";
      errorList.push("Request Type");
    }
    if (!formInputs.businessUnit) {
      errors.businessUnit = "Business Unit is required";
      errorList.push("Business Unit");
    }
    if (!formInputs.itemCategory) {
      errors.itemCategory = "Item Category is required";
      errorList.push("Item Category");
    }
    if (!formInputs.itemDescription?.trim()) {
      errors.itemDescription = "Item Description is required";
      errorList.push("Item Description");
    }
    if (!formInputs.expectedSampleDate) {
      errors.expectedSampleDate = "Expected Sample Date is required";
      errorList.push("Expected Sample Date");
    }

    setValidation(errors);
    setErrorFields(errorList);
    return errorList.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setFailureModal({
        title: "Validation Error",
        msg: "Please fill all mandatory fields before submitting the Sample Request.",
      });
      setShowFailureModal(true);
      return;
    }

    const toastId = toast.loading("Submitting Sample Request...", { position: "top-right" });
    setDisabled(true);

    try {
      const isBrand = formInputs.requestType === "Brand";
      const payload = {
        data: {
          ...formInputs,
          sampleQuantity: Number(formInputs.sampleQuantity) || 1,
          status: isBrand ? "Pending Artwork" : "In QAP",
          currentStep: 1,
          submittedOn: currentDate,
        },
      };

      const res = await ItemService.addSampleRequest(payload);
      toast.dismiss(toastId);

      if (res?.status === 200) {
        setModalInfo({
          title: "Sample Request Created Successfully",
          successMsg:
            res?.message ||
            `Sample Request ${res?.data?.sampleRequestNr || ""} has been created and submitted.`,
        });
        setShowSuccessModal(true);
      } else {
        toast.error(res?.message || "Failed to create Sample Request");
      }
    } catch (err) {
      toast.dismiss(toastId);
      console.error("Error creating sample request:", err);
      toast.error("Error creating Sample Request. Please check backend connection.");
    }

    setDisabled(false);
  };

  const hideSuccessModal = () => {
    setShowSuccessModal(false);
    navigate(Constants.SAMPLE_REQUEST_LIST);
  };

  const hideFailureModal = () => {
    setShowFailureModal(false);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Create Sample Request</h2>
            <p className="subText">Fill Sample Request Details</p>
            <hr />

            {/* Document Header Details */}
            <div className="accordion mb-4" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button" onClick={toggleCardVisibility}>
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">SR</span>
                    </Col>
                    <Col xs={12} md={9} lg={9}>
                      <span className="headerTitle">Header Details</span>
                      <span className="subHeaderTitle">
                        Sample Description and Details
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      {/* Request Type */}
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          id="requestType"
                          label="Request Type"
                          placeholder="Select Request Type"
                          data={getRequestTypeOptions}
                          iconClassName="dropdownIcon"
                          name="requestType"
                          code="requestTypeCode"
                          setValue={formInputs?.requestType || []}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.requestType}
                        />
                      </Col>

                      {/* Business Unit */}
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          id="businessUnit"
                          label="Business Unit"
                          placeholder="Select Business Unit"
                          data={getBusinessUnitOptions}
                          iconClassName="dropdownIcon"
                          name="businessUnit"
                          code="businessUnitCode"
                          setValue={formInputs?.businessUnit || []}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.businessUnit}
                        />
                      </Col>

                      {/* Item Category */}
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          id="itemCategory"
                          label="Item Category"
                          placeholder="Select Item Category"
                          data={getItemCategoryOptions}
                          iconClassName="dropdownIcon"
                          name="itemCategory"
                          code="itemCategoryCode"
                          setValue={formInputs?.itemCategory || []}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.itemCategory}
                        />
                      </Col>

                      {/* Item Description */}
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          id="itemDescription"
                          className="inputBox"
                          label="Item Description"
                          name="itemDescription"
                          placeholder="Enter Item Description"
                          value={formInputs.itemDescription}
                          onChange={onInputChange}
                          required={true}
                          error={validation.itemDescription}
                        />
                      </Col>

                      {/* Expected Sample Date */}
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          id="expectedSampleDate"
                          className="inputBox"
                          label="Expected Sample Date"
                          name="expectedSampleDate"
                          placeholder=""
                          type="date"
                          value={formInputs.expectedSampleDate}
                          onChange={onInputChange}
                          required={true}
                          error={validation.expectedSampleDate}
                        />
                      </Col>

                      {/* Sample Quantity */}
                      <Col
                        xs={12}
                        md={2}
                        lg={2}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          id="sampleQuantity"
                          className="inputBox"
                          label="Sample Quantity"
                          name="sampleQuantity"
                          type="number"
                          placeholder=""
                          value={formInputs.sampleQuantity}
                          onChange={onInputChange}
                        />
                      </Col>

                      {/* UOM */}
                      <Col
                        xs={12}
                        md={2}
                        lg={2}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          id="sampleUom"
                          label="UOM"
                          placeholder="Select UOM"
                          data={getUomOptions}
                          iconClassName="dropdownIcon"
                          name="sampleUom"
                          code="sampleUomCode"
                          setValue={formInputs?.sampleUom || []}
                          getvalue={setDropdownData}
                        />
                      </Col>

                      {/* Remarks */}
                      <Col
                        xs={12}
                        md={12}
                        lg={12}
                        className="commTopButtonRightLeftPadding"
                      >
                        <TextAreaField
                          id="remarks"
                          className="inputBox"
                          label="Remarks / Specifications"
                          name="remarks"
                          placeholder="Enter special requirements or remarks"
                          value={formInputs.remarks}
                          onChange={onInputChange}
                          rows={3}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Page Footer Buttons */}
            <div className="pageFooter mb-4 pb-2">
              <Row className="rowDirection">
                <Col xs={12} md={2} lg={2} className="">
                  <button
                    className="btnTable"
                    onClick={handleSubmit}
                    disabled={disabled}
                  >
                    <i className="bi bi-save"></i> {disabled ? "Submitting..." : "Submit"}
                  </button>
                </Col>
                <Col xs={12} md={2} lg={2} className="">
                  <button
                    className="btnTable"
                    onClick={() => navigate(Constants.SAMPLE_REQUEST_LIST)}
                  >
                    <i className="bi bi-x-circle"></i> Cancel
                  </button>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>

      {/* Success Modal matching CreatePR */}
      <SuccessRegisterModal
        show={getShowSuccessModal}
        onHide={() => hideSuccessModal()}
        modaldata={getModalInfo}
        aftersuccess={Constants.SAMPLE_REQUEST_LIST}
      />

      {/* Failure Modal matching CreatePR */}
      <FailureModal
        show={getShowFailureModal}
        onHide={() => hideFailureModal()}
        failuremodaldata={getFailureModal}
        requiredfields={getErrorFields}
      />
    </main>
  );
};

export default CreateSampleRequest;
